import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle } from "@phosphor-icons/react";

/**
 * VacancyBulk — one-shot updater for a property's per-unit vacancy state.
 * Route: /admin/properties/:address/vacancy-bulk
 * Fixes the "everything is stale-defaulted to occupied" problem in 90 seconds.
 */
const STATUSES = [
  { key: "occupied",      label: "Occupied",      color: "border-emerald-500 bg-emerald-50 text-emerald-800" },
  { key: "vacant",        label: "Vacant",        color: "border-amber-500 bg-amber-50 text-amber-800" },
  { key: "tenant_pulled", label: "Tenant Pulled", color: "border-red-500 bg-red-50 text-red-800" },
  { key: "unfit",         label: "Unfit",         color: "border-slate-500 bg-slate-100 text-slate-800" },
];

export default function VacancyBulk() {
  const { address: encoded } = useParams();
  const navigate = useNavigate();
  const address = decodeURIComponent(encoded || "");
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/properties/detail?address=${encodeURIComponent(address)}`)
      .then(r => {
        const units = (r.data.units || []).filter(u => (u.unit || "").trim());
        setRows(units.map(u => ({
          unit: u.unit,
          vacancy_status: (u.vacancy_status || "occupied").toLowerCase(),
          _touched: false,
        })));
      })
      .catch(() => toast.error("Failed to load property"))
      .finally(() => setLoading(false));
  }, [address]);

  const setStatus = (i, s) => {
    const next = [...rows];
    next[i] = { ...next[i], vacancy_status: s, _touched: true };
    setRows(next);
  };
  const setAll = (s) => setRows(rows.map(r => ({ ...r, vacancy_status: s, _touched: true })));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post("/properties/vacancy-bulk", {
        address, updates: rows.map(({ unit, vacancy_status }) => ({ unit, vacancy_status })),
      });
      toast.success(`Updated ${r.data.updated} units`);
      navigate(`/admin/properties/${encodeURIComponent(address)}/snapshot`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed"));
    } finally { setSaving(false); }
  };

  return (
    <AdminShell>
      <div className="p-6 max-w-3xl mx-auto" data-testid="vacancy-bulk-page">
        <Link to={`/admin/properties/${encodeURIComponent(address)}`}
          className="text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-[#0f172a] inline-flex items-center gap-1">
          <ArrowLeft size={12}/> Back to property
        </Link>
        <h1 className="mt-2 font-display font-black text-3xl uppercase tracking-tight">
          Bulk Vacancy Update
        </h1>
        <div className="text-sm text-slate-600 mt-1">{address} · {rows.length} units</div>

        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Set all →</div>
          {STATUSES.map(s => (
            <button key={s.key} onClick={() => setAll(s.key)} data-testid={`vacancy-setall-${s.key}`}
              className={`px-2 py-1 text-[10px] font-black uppercase tracking-widest border-2 ${s.color} hover:brightness-95`}>
              {s.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="mt-6 text-slate-500 font-black uppercase text-xs tracking-widest">Loading…</div>
        ) : (
          <div className="mt-4 rugged-card p-3 bg-white space-y-1">
            {rows.map((r, i) => (
              <div key={r.unit} data-testid={`vacancy-row-${r.unit}`}
                className="flex items-center gap-2 p-2 border-b border-slate-100 last:border-b-0">
                <div className="font-mono font-black text-sm min-w-[60px]">{r.unit}</div>
                <div className="flex-1 flex items-center gap-1 flex-wrap">
                  {STATUSES.map(s => {
                    const on = r.vacancy_status === s.key;
                    return (
                      <button key={s.key} onClick={() => setStatus(i, s.key)}
                        data-testid={`vacancy-${r.unit}-${s.key}`}
                        className={`px-2 py-1 text-[10px] font-black uppercase tracking-widest border-2 transition-colors ${on ? s.color : "border-slate-200 bg-white text-slate-500 hover:border-slate-400"}`}>
                        {s.label}
                      </button>
                    );
                  })}
                </div>
                {r._touched && <CheckCircle weight="fill" size={14} className="text-cyan-600 flex-shrink-0"/>}
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 sticky bottom-0 bg-white p-3 border-t-2 border-[#0f172a] -mx-6">
          <button onClick={save} disabled={saving || loading}
            data-testid="vacancy-bulk-save"
            className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-sm tracking-widest border-2 border-[#0f172a] disabled:opacity-50">
            {saving ? "Saving…" : `Save all ${rows.length} units`}
          </button>
        </div>
      </div>
    </AdminShell>
  );
}
